import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { cleanupAuthState, markSigningOut } from '@/lib/authCleanup';
import { setSentryUser } from '@/lib/sentry';
import { useRenderDebug } from '@/hooks/useRenderDebug';

/**
 * True when the app points at a LOCAL Supabase stack (the devcontainer). Used to
 * enable a dev-only email/password login and to skip the Keycloak logout
 * redirect. Never true in production, which uses the hosted *.supabase.co URL.
 */
export const isLocalSupabase = /localhost|127\.0\.0\.1/.test(
  (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? ''
);

interface AuthContextType {
  user: User | null;
  hasSession: boolean;
  signInWithKeycloak: (silent?: boolean) => Promise<{ error: AuthError | null }>;
  /** Dev-only (local Supabase) email/password sign-in. */
  signInWithPassword: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  checkAuthSilently: () => Promise<{ user: User | null; error: string | null }>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // Only track whether session exists (null vs non-null) to avoid re-renders on token refresh.
  // The Supabase client manages its own session internally — consumers should use
  // supabase.auth.getSession() when they need the actual token.
  const [hasSession, setHasSession] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastUserRef = useRef<{ id: string; updated_at: string } | null>(null);

  useEffect(() => {
    let mounted = true;

    // Only update user state and Sentry if the user actually changed
    const setUserStable = (newUser: User | null) => {
      const prevId = lastUserRef.current?.id;
      const prevUpdatedAt = lastUserRef.current?.updated_at;
      if (prevId === newUser?.id && prevUpdatedAt === newUser?.updated_at) return;

      lastUserRef.current = newUser
        ? { id: newUser.id, updated_at: newUser.updated_at ?? '' }
        : null;
      setUser(newUser);
      setSentryUser(newUser ? { id: newUser.id, email: newUser.email } : null);
    };

    const updateSession = (newSession: Session | null) => {
      // Only trigger re-render when going null↔non-null
      setHasSession(prev => {
        const newHas = newSession !== null;
        return prev === newHas ? prev : newHas;
      });
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return;
        updateSession(session);
        setUserStable(session?.user ?? null);
        setLoading(false);
      }
    );

    // Check for existing session
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (!mounted) return;

        // If getSession returns an error (e.g. expired JWT, invalid refresh token),
        // the session from localStorage is stale — clean up and treat as signed out
        if (error) {
          cleanupAuthState();
          updateSession(null);
          setUserStable(null);
          setLoading(false);
          return;
        }

        updateSession(session);
        setUserStable(session?.user ?? null);
        setLoading(false);
      } catch (err) {
        if (mounted) {
          cleanupAuthState();
          updateSession(null);
          setUserStable(null);
          setLoading(false);
        }
      }
    };

    checkSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signInWithKeycloak = useCallback(async (silent = false) => {
    const redirectUrl = `${window.location.origin}/auth/callback`;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'keycloak',
      options: {
        redirectTo: redirectUrl,
        scopes: 'openid profile email',
        skipBrowserRedirect: silent,
      }
    });

    return { error };
  }, []);

  // Dev-only email/password sign-in against the local Supabase stack. The only
  // caller (Auth page) gates this behind isLocalSupabase, so it never runs in
  // production. Local Supabase has email auth enabled with confirmations off.
  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const checkAuthSilently = useCallback(async () => {
    try {
      // First check if we have an existing session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        return { user: null, error: sessionError.message };
      }

      if (session?.user) {
        return { user: session.user, error: null };
      }

      // For Supabase OAuth with Keycloak backend, try silent auth without redirect
      const { error } = await signInWithKeycloak(true);

      if (error) {
        return { user: null, error: error.message || 'Silent authentication failed' };
      }

      return { user: null, error: 'Silent authentication requires user interaction' };
    } catch (err) {
      return { user: null, error: 'Silent authentication failed' };
    }
  }, [signInWithKeycloak]);

  const signOut = useCallback(async () => {
    try {
      // Clearing the Supabase session re-renders straight through to /login,
      // which auto-starts a Keycloak SSO round-trip. That request restarts the
      // root auth session and invalidates the session_code behind Keycloak's
      // logout confirmation page, so the logout below dies with "Logout
      // failed". Mark the sign-out (in every tab) so the login page shows a
      // "signed out" button instead of auto-starting SSO.
      markSigningOut();
      // Attempt global sign out (revoke refresh tokens). This must run BEFORE
      // cleanupAuthState(): auth-js reads the session out of storage to find
      // the access token, and skips the server call entirely when it is gone,
      // so clearing storage first turns scope:'global' into a silent no-op.
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // ignore errors to ensure we still redirect
      }
      // Clean any auth keys left in local/session storage to avoid limbo states
      cleanupAuthState();
    } finally {
      // Local dev has no Keycloak session — just return to the local login page.
      if (isLocalSupabase) {
        window.location.href = `${window.location.origin}/login`;
        return;
      }
      // End Keycloak session too — redirect to Keycloak logout endpoint
      // which clears the Keycloak session cookie and redirects back to the hub login page
      const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL || 'https://admin.relevee.nl';
      const keycloakRealm = import.meta.env.VITE_KEYCLOAK_REALM || 'relevee';
      const keycloakClientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'releve-financial-hub';
      // ?signedout=1 marks the tab coming back from a completed logout, so the
      // login page shows "you are signed out" rather than bouncing back into
      // Keycloak — and does so even if the sign-out marker has lapsed because
      // the confirmation page sat open for a while.
      const postLogoutRedirect = encodeURIComponent(`${window.location.origin}/login?signedout=1`);
      window.location.href = `${keycloakUrl}/realms/${keycloakRealm}/protocol/openid-connect/logout?post_logout_redirect_uri=${postLogoutRedirect}&client_id=${keycloakClientId}`;
    }
  }, []);

  useRenderDebug('AuthProvider', {
    user,
    hasSession,
    loading,
    signInWithKeycloak,
    signInWithPassword,
    checkAuthSilently,
    signOut,
  });

  const value = useMemo(() => ({
    user,
    hasSession,
    signInWithKeycloak,
    signInWithPassword,
    checkAuthSilently,
    signOut,
    loading
  }), [user, hasSession, signInWithKeycloak, signInWithPassword, checkAuthSilently, signOut, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}